import React, { ChangeEvent } from "react";

const handleFileWithSizeLimit = async (
  e: ChangeEvent<HTMLInputElement>,
  maxSize = 10 * 1024 * 1024,
  callback: (event: ChangeEvent<HTMLInputElement>) => void
) => {
  const selectedFiles = e.target.files;

  // File name must start with a letter or number
  // and can include letters, numbers, spaces, dots, dashes, underscores, parentheses
  const fileNameRegex = /^[a-zA-Z0-9][a-zA-Z0-9\s._()\-]*$/;

  // Allowed MIME types and extensions
  const allowedMimeTypes = ["image/jpeg", "image/png", "application/pdf"];
  // const allowedExtensions = ["jpg", "jpeg", "png", "pdf"];

  if (selectedFiles) {
    for (let i = 0; i < selectedFiles.length; i++) {
      const file = selectedFiles[i];
      const fileName = file.name.toLowerCase();

      // 🔐 File size check
      if (file.size > maxSize) {
        alert(`File size for "${file.name}" exceeds the limit.`);
        e.target.value = "";
        return;
      }

      // 🧪 Filename character check
      if (!fileNameRegex.test(file.name)) {
        alert(
          `File name "${file.name}" is invalid. It must start with a letter or number and can only contain letters, numbers, spaces, dots (.), dashes (-), underscores (_), and parentheses ().`
        );
        e.target.value = "";
        return;
      }

      // Extract extension and validate safely
      const nameParts = fileName.toLowerCase().split(".");
      const fileExtension = nameParts[nameParts.length - 1];
      const allowedExtensions = ["jpg", "jpeg", "png", "pdf"];
      const disallowedExtensions = [
        "exe",
        "php",
        "js",
        "sh",
        "bat",
        "cmd",
        "vbs",
        "html",
        "htm",
        "jar",
        "py",
        "rb",
        "c",
        "cpp",
        "pl",
      ];

      // Check for valid extension at the end
      const isExtensionAllowed = allowedExtensions.includes(fileExtension);

      // Check if any disallowed extension exists before the final extension
      const containsBlockedExtension = nameParts
        .slice(0, -1)
        .some((ext) => disallowedExtensions.includes(ext));

      const isValidFile =
        nameParts.length >= 2 &&
        isExtensionAllowed &&
        !containsBlockedExtension;

      if (!isValidFile) {
        alert(
          `File "${file.name}" has an invalid or suspicious extension. Only JPG, JPEG, PNG, and PDF files are allowed.`
        );
        e.target.value = "";
        return;
      }

      // MIME type check (by file header)
      const mimeType = await getFileMimeType(file);
      if (!allowedMimeTypes.includes(mimeType)) {
        alert(`File "${file.name}" is not an accepted file type.`);
        e.target.value = "";
        return;
      }
    }
  }

  //  All validations passed
  callback(e);
};

// 📄 Helper to read the file's MIME type from binary content
const getFileMimeType = (file: File): Promise<string> => {
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      const arr = new Uint8Array(reader.result as ArrayBuffer).subarray(0, 4);
      let header = "";
      for (let i = 0; i < arr.length; i++) {
        header += arr[i].toString(16);
      }

      // File signature magic numbers
      switch (header) {
        case "89504e47":
          resolve("image/png");
          break;
        case "ffd8ffe0":
        case "ffd8ffe1":
        case "ffd8ffe2":
        case "ffd8ffe3":
        case "ffd8ffe8":
          resolve("image/jpeg");
          break;
        case "25504446":
          resolve("application/pdf");
          break;
        default:
          resolve("unknown");
          break;
      }
    };
    reader.readAsArrayBuffer(file);
  });
};

export default handleFileWithSizeLimit;
